import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Copy, Trash2, Wifi, WifiOff, RefreshCw, Phone, Users, Monitor, Smartphone } from "lucide-react";
import { toast } from "sonner";

const API = '/api';

// Helper: check connection (heartbeat < 45s ago)
const isReallyConnected = (profile) => {
  if (profile.status !== 'connected') return false;
  if (!profile.last_seen) return false;
  const diffSeconds = (new Date() - new Date(profile.last_seen)) / 1000;
  return diffSeconds < 45;
};

const getLastSeenText = (profile) => {
  if (!profile.last_seen) return null;
  const secs = Math.floor((new Date() - new Date(profile.last_seen)) / 1000);
  if (secs < 5) return 'ahora';
  if (secs < 60) return `hace ${secs}s`;
  if (secs < 3600) return `hace ${Math.floor(secs/60)}m`;
  return `hace ${Math.floor(secs/3600)}h`;
};

export default function Profiles() {
  const [profiles, setProfiles] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", phone_number: "", type: "chrome" });
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0); // forces re-render every second for live countdown

  const fetchProfiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profiles`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      setProfiles(res.data);
    } catch (e) {
      // error silenced
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
    const pollInterval = setInterval(fetchProfiles, 10000); // refresh data every 10s
    const tickInterval = setInterval(() => setTick(t => t + 1), 1000); // tick every 1s for live timer
    return () => {
      clearInterval(pollInterval);
      clearInterval(tickInterval);
    };
  }, [fetchProfiles]);

  const createProfile = async () => {
    if (!form.name) return toast.error("El nombre es obligatorio");
    try {
      await axios.post(`${API}/profiles`, form);
      toast.success("Perfil creado correctamente");
      setShowCreate(false);
      setForm({ name: "", phone_number: "", type: "chrome" });
      fetchProfiles();
    } catch (e) {
      toast.error("Error al crear perfil");
    }
  };

  const deleteProfile = async (id) => {
    try {
      await axios.delete(`${API}/profiles/${id}`);
      toast.success("Perfil eliminado");
      fetchProfiles();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    toast.success("Token copiado al portapapeles");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="profiles-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white uppercase">
            Perfiles
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Gestiona las conexiones de extensiones Chrome y APKs Android</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="refresh-profiles-btn"
            onClick={fetchProfiles}
            className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 rounded-md px-3 py-2 transition-colors duration-200"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            data-testid="create-profile-btn"
            onClick={() => setShowCreate(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-[0_0_15px_rgba(37,99,235,0.3)] rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors duration-200 active:scale-95"
          >
            <Plus className="w-4 h-4" /> Nuevo Perfil
          </button>
        </div>
      </div>

      {showCreate && (
        <div data-testid="create-profile-form" className="bg-zinc-900 border border-white/10 rounded-lg p-5 space-y-4 animate-fade-in">
          <h3 className="font-heading text-lg font-semibold text-white uppercase">Crear Perfil</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Nombre</label>
              <input
                data-testid="profile-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200"
                placeholder="Ej: Linea Principal"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Telefono</label>
              <input
                data-testid="profile-phone-input"
                value={form.phone_number}
                onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200"
                placeholder="+521234567890"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Tipo</label>
              <select
                data-testid="profile-type-select"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none transition-colors duration-200"
              >
                <option value="chrome">Chrome (RCS)</option>
                <option value="whatsapp_web">Chrome (WhatsApp Web)</option>
                <option value="android">Android (WhatsApp)</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              data-testid="save-profile-btn"
              onClick={createProfile}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200 active:scale-95"
            >
              Guardar
            </button>
            <button
              data-testid="cancel-profile-btn"
              onClick={() => setShowCreate(false)}
              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-md px-4 py-2 text-sm transition-colors duration-200"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {profiles.map((p, i) => {
          const connected = isReallyConnected(p);
          const lastSeenText = getLastSeenText(p);
          return (
          <div
            key={p.id}
            data-testid={`profile-card-${i}`}
            className={`bg-zinc-900 border rounded-lg p-5 space-y-4 hover:border-white/10 transition-colors duration-200 animate-fade-in ${
              connected ? 'border-emerald-500/20' : 'border-white/5'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full ${
                  connected ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-600'
                }`} />
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-sm font-semibold text-white">{p.name}</h3>
                    {p.type === 'android' ? (
                      <span className="text-[9px] bg-green-500/15 text-green-400 border border-green-500/30 rounded px-1.5 py-0.5 font-mono">WA</span>
                    ) : p.type === 'whatsapp_web' ? (
                      <span className="text-[9px] bg-green-500/15 text-green-400 border border-green-500/30 rounded px-1.5 py-0.5 font-mono">WA Web</span>
                    ) : (
                      <span className="text-[9px] bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded px-1.5 py-0.5 font-mono">RCS</span>
                    )}
                    {p.client_type === 'desktop' && (
                      <span className="text-[9px] bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 rounded px-1.5 py-0.5 font-mono" data-testid={`profile-desktop-badge-${p.id}`}>Escritorio</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Phone className="w-3 h-3 text-zinc-500" />
                    <span className="text-xs font-mono text-zinc-500">{p.phone_number || "Sin numero"}</span>
                  </div>
                  {lastSeenText && (
                    <span className={`text-[9px] font-mono mt-0.5 block ${connected ? 'text-emerald-400' : 'text-zinc-600'}`}>
                      {connected ? `● Conectado · ${lastSeenText}` : `Desconectado · ${lastSeenText}`}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                {connected ? (
                  <Wifi className="w-4 h-4 text-emerald-400" />
                ) : (
                  <WifiOff className="w-4 h-4 text-zinc-600" />
                )}
                {connected && p.extension_version && (
                  <span className="text-[9px] font-mono text-zinc-500">v{p.extension_version}</span>
                )}
              </div>
            </div>

            <div className="bg-zinc-950/50 rounded-md p-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mb-1">
                Token {p.type === 'android' ? 'de APK' : 'de Extension'}
              </p>
              <div className="flex items-center gap-2">
                <code className="text-xs font-mono text-cyan-500/80 truncate flex-1">
                  {p.extension_token}
                </code>
                <button
                  data-testid={`copy-token-${i}`}
                  onClick={() => copyToken(p.extension_token)}
                  className="text-zinc-500 hover:text-white transition-colors duration-200"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs">
              <div>
                <span className="text-zinc-600">Hoy: </span>
                <span className="text-zinc-300 font-mono">{p.messages_sent_today || 0}</span>
                <span className="text-zinc-600"> / </span>
                <span className="text-zinc-400 font-mono">{p.daily_limit_override || p.daily_limit || '50'}</span>
              </div>
              <div>
                <span className="text-zinc-600">Total: </span>
                <span className="text-zinc-300 font-mono">{p.total_messages_sent || 0}</span>
              </div>
            </div>

            <div className="flex gap-2 pt-1 border-t border-white/5">
              <button
                data-testid={`delete-profile-${i}`}
                onClick={() => deleteProfile(p.id)}
                className="bg-red-500/10 text-red-500 hover:bg-red-500/20 border border-red-500/20 rounded-md px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors duration-200"
              >
                <Trash2 className="w-3 h-3" /> Eliminar
              </button>
            </div>
          </div>
        );
        })}
        {profiles.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No hay perfiles creados</p>
            <p className="text-zinc-600 text-xs mt-1">Crea un perfil para conectar una extension Chrome</p>
          </div>
        )}
      </div>
    </div>
  );
}
